import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiCheck, FiX, FiMessageCircle } from 'react-icons/fi';
import commentService from '../../services/commentService';
import articleService from '../../services/articleService';

const ModerationContainer = styled.div`
  padding: 2rem 0;
`;

const ModerationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const ModerationTitle = styled.h1`
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.divider};
  border-radius: 8px;
  padding: 1.5rem;
  text-align: center;
  
  .stat-number {
    font-size: 2rem;
    font-weight: 600;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 0.5rem;
  }
  
  .stat-label {
    color: ${props => props.theme.colors.textSecondary};
    font-size: 0.9rem;
  }
`;

const CommentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CommentCard = styled.div`
  background: white;
  border: 1px solid ${props => props.theme.colors.divider};
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const CommentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  
  .comment-info {
    .author {
      font-weight: 600;
      color: ${props => props.theme.colors.text};
      margin-bottom: 0.25rem;
    }
    
    .article-title {
      font-size: 0.9rem;
      color: ${props => props.theme.colors.primary};
      margin-bottom: 0.25rem;
    }
    
    .date {
      font-size: 0.8rem;
      color: ${props => props.theme.colors.textSecondary};
    }
  }
  
  .comment-actions {
    display: flex;
    gap: 0.5rem;
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &.approve {
    background: #e8f5e9;
    color: #2e7d32;
    
    &:hover {
      background: #c8e6c9;
    }
  }
  
  &.reject {
    background: #ffebee;
    color: #d32f2f;
    
    &:hover {
      background: #ffcdd2;
    }
  }
`;

const CommentContent = styled.div`
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.divider};
  border-radius: 4px;
  padding: 1rem;
  color: ${props => props.theme.colors.text};
  line-height: 1.6;
  white-space: pre-wrap;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 0;
  color: ${props => props.theme.colors.textSecondary};
  
  .empty-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
    opacity: 0.5;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 2rem 0;
  color: ${props => props.theme.colors.textSecondary};
`;

const CommentModeration = () => {
  const [pendingComments, setPendingComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [articles, setArticles] = useState({});

  useEffect(() => {
    loadPendingComments();
  }, []);

  const loadPendingComments = async () => {
    try {
      setLoading(true);
      const comments = await commentService.getPendingComments();
      setPendingComments(comments);
      
      // Load article titles for each comment
      const articleIds = [...new Set(comments.map(c => c.articleId))];
      const articleData = {};
      
      for (const articleId of articleIds) {
        try {
          const article = await articleService.getArticle(articleId);
          articleData[articleId] = article;
        } catch (error) {
          console.error(`Error loading article ${articleId}:`, error);
          articleData[articleId] = { title: 'Unknown Article' };
        }
      }
      
      setArticles(articleData);
    } catch (error) {
      console.error('Error loading pending comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveComment = async (commentId) => {
    try {
      setProcessing(commentId);
      await commentService.approveComment(commentId);
      setPendingComments(prev => prev.filter(c => c.id !== commentId));
    } catch (error) {
      console.error('Error approving comment:', error);
      alert('Failed to approve comment');
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectComment = async (commentId) => {
    try {
      setProcessing(commentId);
      await commentService.deleteComment(commentId);
      setPendingComments(prev => prev.filter(c => c.id !== commentId));
    } catch (error) {
      console.error('Error rejecting comment:', error);
      alert('Failed to reject comment');
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <ModerationContainer>
        <div className="container">
          <LoadingState>Loading pending comments...</LoadingState>
        </div>
      </ModerationContainer>
    );
  }

  return (
    <ModerationContainer>
      <div className="container">
        <ModerationHeader>
          <ModerationTitle>
            <FiMessageCircle size={28} />
            Comment Moderation
          </ModerationTitle>
        </ModerationHeader>

        <StatsContainer>
          <StatCard>
            <div className="stat-number">{pendingComments.length}</div>
            <div className="stat-label">Pending Comments</div>
          </StatCard>
        </StatsContainer>

        {pendingComments.length > 0 ? (
          <CommentsList>
            {pendingComments.map((comment) => (
              <CommentCard key={comment.id}>
                <CommentHeader>
                  <div className="comment-info">
                    <div className="author">{comment.author}</div>
                    <div className="article-title">
                      On: {comment.articles?.title || articles[comment.article_id]?.title || 'Loading...'}
                    </div>
                    <div className="date">{formatDate(comment.created_at)}</div>
                  </div>
                  <div className="comment-actions">
                    <ActionButton
                      className="approve"
                      onClick={() => handleApproveComment(comment.id)}
                      disabled={processing === comment.id}
                    >
                      <FiCheck size={16} />
                      Approve
                    </ActionButton>
                    <ActionButton
                      className="reject"
                      onClick={() => handleRejectComment(comment.id)}
                      disabled={processing === comment.id}
                    >
                      <FiX size={16} />
                      Reject
                    </ActionButton>
                  </div>
                </CommentHeader>
                <CommentContent>{comment.content}</CommentContent>
              </CommentCard>
            ))}
          </CommentsList>
        ) : (
          <EmptyState>
            <div className="empty-icon">
              <FiMessageCircle />
            </div>
            <h3>No pending comments</h3>
            <p>All comments have been reviewed.</p>
          </EmptyState>
        )}
      </div>
    </ModerationContainer>
  );
};

export default CommentModeration;